import chunk from 'lodash/chunk';
import uniqueId from 'lodash/uniqueId';

import { getStringFromObjectsList } from 'utils/formatters';
import { DetailedInventoryAsset } from 'components/common/types/DetailedInventoryAsset.types';
import Timeline from 'lib/Timeline';
import { SidebarSections } from 'components/pages/InventoryManagement/AssetList/AssetSidebar/AssetSidebar.types';

import PriceInfo from '../Prices/PriceInfo';
import ProhibitionInfo from '../Prohibitions/ProhibitionInfo';
import TimeModelInfo from '../TimeModels/TimeModelInfo';
import NetworkInfo from '../Networks/NetworkInfo';
import TagInfo from '../Tags/TagInfo';
import Panel from './Panel';

type ReadMoreSectionsProps = {
  asset?: DetailedInventoryAsset | null;
  openSections: SidebarSections;
  toggleReadMoreSection: (sectionKey: keyof SidebarSections) => void;
};

const ReadMoreSections = ({
  asset = null,
  toggleReadMoreSection,
  openSections,
}: ReadMoreSectionsProps): React.ReactElement => {
  return (
    <>
      {asset?.prices?.length ? (
        <Panel isOpen={openSections.prices} toggle={() => toggleReadMoreSection('prices')} title="Prices">
          <Timeline>
            {asset.prices.map((price) => (
              <PriceInfo key={`${price.startDate}${price.startOffset}${price.priceType}`} price={price} />
            ))}
          </Timeline>
        </Panel>
      ) : null}
      {asset?.prohibitions?.length ? (
        <Panel
          isOpen={openSections.prohibitions}
          toggle={() => toggleReadMoreSection('prohibitions')}
          title="Prohibitions"
        >
          <Timeline>
            {chunk(asset.prohibitions, 3).map((prohibitionGroup) => (
              <ProhibitionInfo
                key={`${asset.routeFrameCode}, ${getStringFromObjectsList(prohibitionGroup, 'productCategoryUuid')}`}
                prohibitionGroup={prohibitionGroup}
              />
            ))}
          </Timeline>
        </Panel>
      ) : null}
      {asset?.timeModels?.length ? (
        <Panel isOpen={openSections.timeModels} toggle={() => toggleReadMoreSection('timeModels')} title="Time models">
          <Timeline>
            {asset.timeModels.map((model) => (
              <TimeModelInfo key={uniqueId('readMoreTimeModel_')} model={model} />
            ))}
          </Timeline>
        </Panel>
      ) : null}
      {asset?.networks?.length ? (
        <Panel isOpen={openSections.networks} toggle={() => toggleReadMoreSection('networks')} title="Networks">
          <Timeline>
            {asset.networks.map((network) => (
              <NetworkInfo key={network.networkUuid} network={network} />
            ))}
          </Timeline>
        </Panel>
      ) : null}
      {asset?.tags?.length ? (
        <Panel isOpen={openSections.tags} toggle={() => toggleReadMoreSection('tags')} title="Tags">
          <Timeline>
            {chunk(asset.tags, 3).map((tagGroup) => (
              <TagInfo key={getStringFromObjectsList(tagGroup, 'tagUuid')} tagGroup={tagGroup} />
            ))}
          </Timeline>
        </Panel>
      ) : null}
    </>
  );
};

export default ReadMoreSections;
